import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PosPlanCard from './PosPlanCard';
import StringListInput from './StringListInput';
import PosCheckoutTabPending from './PosCheckoutTabPending';
import PosSubscribeConfirmation from './PosSubscribeConfirmation';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { useCheckoutTab } from '../hooks/useCheckoutTab';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  subscribeSubscriptionPlans,
  subscribeAdminSettings,
  clientSelectablePlans,
  cleanExtraStaffEmails,
  computeApplicationTotal,
  DEFAULT_ADMIN_SETTINGS,
} from '../utils/posSubscriptionUtil';
import { clientEmailDomain } from '../utils/posClientUtil';
import { POS_EXTRA_STAFF_MAX } from '../constants/posConst';
import { CHECKOUT_PATH_SEGMENT } from '../constants/bookingConst';
import '../css/Pos.css';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/** The steps, in order. */
const STEPS = [
  { key: 'plans', label: 'Choose a plan' },
  { key: 'staff', label: 'Staff accounts' },
  { key: 'checkout', label: 'Checkout' },
];

/**
 * Subscribe / Resubscribe, opened from the till's subscription banner.
 *
 *   1. Choose a plan      — the plans an administrator offers (no trials)
 *   2. Staff accounts     — optional extra accounts, each priced per plan
 *   3. Checkout           — in a NEW BROWSER TAB (PosSubscribeCheckout at
 *                           /<client-id>/checkout/<session>): total, our QR,
 *                           upload the receipt, submit. Meanwhile this dialog
 *                           shows PosCheckoutTabPending.
 *   4. Confirmation       — back here: the checkout tab submits, reports the
 *                           application and closes itself, and this dialog
 *                           shows what happens next.
 *
 * The court-booking system's checkout flow (hooks/useCheckoutTab). × in the
 * checkout tab, or Back here, returns to the staff step with everything
 * still filled in; closing this dialog closes the checkout tab with it.
 *
 * Nothing is written until Submit in the checkout tab. Closes on the × only —
 * never on a backdrop tap, which would throw away a chosen plan.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} [props.initialPlanId] - a plan already picked elsewhere
 *   (the Subscriptions screen). The dialog then opens on the staff step with
 *   it chosen; Back still leads to the full plan list.
 * @returns {JSX.Element|null}
 */
function PosSubscribeModal({ isOpen, onClose, initialPlanId = '' }) {
  const { config, clientId } = useConfig();
  const { staffEmail } = usePosSession();

  const [plans, setPlans] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState('plans');
  const [planId, setPlanId] = useState('');
  const [extraEmails, setExtraEmails] = useState([]);
  // True while checkout is open in its own tab.
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);

  const checkoutTab = useCheckoutTab({
    // Submitted in the checkout tab, which is closing: show the confirmation.
    onSubmitted: (payload) => {
      setCheckingOut(false);
      if (payload?.application) {
        setSubmitted(payload.application);
        setStep('done');
      }
    },
    // The × in the checkout tab: back to the staff step, nothing sent.
    onCancelled: () => setCheckingOut(false),
  }, { routePath: `/${clientId}/${CHECKOUT_PATH_SEGMENT}` });
  const endCheckout = checkoutTab.end;

  // Fresh every time it opens: a plan chosen, abandoned and reopened a day
  // later should not still be half-way through checkout.
  useEffect(() => {
    if (!isOpen) return undefined;
    setStep(initialPlanId ? 'staff' : 'plans');
    setPlanId(initialPlanId);
    setExtraEmails([]);
    setCheckingOut(false);
    setError('');
    setSubmitted(null);
    setPlans(null);
    setSettings(null);
    setLoadError(null);

    const stopPlans = subscribeSubscriptionPlans(
      (list) => setPlans(clientSelectablePlans(list)),
      (loadFailure) => { setLoadError(loadFailure); setPlans([]); },
    );
    const stopSettings = subscribeAdminSettings(
      setSettings,
      () => setSettings(DEFAULT_ADMIN_SETTINGS),
    );
    // Closing the dialog closes a checkout tab still open from it.
    return () => { stopPlans(); stopSettings(); endCheckout(); };
  }, [isOpen, initialPlanId, endCheckout]);

  const plan = useMemo(() => (plans || []).find((option) => option.id === planId) || null, [plans, planId]);
  const typedCount = extraEmails.filter((value) => String(value).trim()).length;
  const running = settings ? computeApplicationTotal(plan, typedCount, settings.extraStaffPrice) : null;

  if (!isOpen) return null;

  const loading = plans === null || settings === null;

  /**
   * Validates the extra staff and opens checkout in a new tab with the order
   * in it. Runs inside the click: browsers only allow window.open during one.
   * @returns {void}
   */
  const handleStaffContinue = () => {
    try {
      const extraStaffEmails = cleanExtraStaffEmails(clientId, extraEmails);
      setError('');
      checkoutTab.open({
        clientId,
        plan,
        extraStaffEmails,
        // The price this dialog quoted, carried over so the checkout total
        // is the one the client agreed to.
        extraStaffPrice: settings.extraStaffPrice,
        submittedBy: staffEmail,
      });
      setCheckingOut(true);
    } catch (validationError) {
      setError(validationError.message);
    }
  };

  /** Backs out of checkout from this dialog: closes the tab. @returns {void} */
  const handleCheckoutBack = () => {
    checkoutTab.end();
    setCheckingOut(false);
  };

  // Checkout counts as the third step while its tab is open.
  const stepIndex = checkingOut ? 2 : STEPS.findIndex((entry) => entry.key === step);

  /** @returns {JSX.Element} the current step's body */
  const renderBody = () => {
    if (loading) return <div className="pa-loading pa-loading-inline">{spinnerRing('Loading plans')}</div>;

    if (step === 'done' && submitted) return <PosSubscribeConfirmation application={submitted} config={config} />;

    if (checkingOut) {
      return (
        <PosCheckoutTabPending
          tabState={checkoutTab.tabState}
          onOpenCheckout={checkoutTab.reopen}
          onBack={handleCheckoutBack}
          submitLabel="Submit application"
          backLabel="Back to staff accounts"
        />
      );
    }

    if (step === 'plans') {
      if (loadError) return <p className="pa-role-error">The plans could not be loaded: {loadError.message}</p>;
      if (plans.length === 0) {
        return <p className="ps-muted">No plans are available right now. Please contact The Website Pal.</p>;
      }
      return (
        <div className="ps-plan-grid">
          {plans.map((option) => (
            <PosPlanCard
              key={option.id}
              plan={option}
              config={config}
              selected={option.id === planId}
              onSelect={() => setPlanId(option.id)}
              selectLabel="Choose plan"
            />
          ))}
        </div>
      );
    }

    // The staff step — the last one this dialog shows before checkout.
    // "3-Months Plan" already says plan; "Monthly" needs the word.
    const planLabel = /plan$/i.test(plan.name.trim()) ? plan.name : `${plan.name} plan`;
    return (
      <div className="ps-staff-step">
        <h4 className="ps-step-title">Want additional staff accounts?</h4>
        <p className="ps-muted">
          {plan.freeStaff > 0 && (
            <>
              Your {planLabel} includes <strong>{plan.freeStaff} free staff account{plan.freeStaff === 1 ? '' : 's'}</strong>.
              {' '}Each account beyond that is {formatMoney(settings.extraStaffPrice, config)} for the whole length of the plan.
            </>
          )}
          {!plan.freeStaff && (
            <>
              Each additional staff account is {formatMoney(settings.extraStaffPrice, config)} for the
              whole length of your {planLabel}.
            </>
          )}
          {' '}Enter the email address each person will sign in with, or skip this step if you
          don&rsquo;t need any.
        </p>
        <StringListInput
          values={extraEmails}
          onChange={(values) => { setExtraEmails(values); setError(''); }}
          placeholder={`name@${clientEmailDomain(clientId)}`}
          addLabel="Add another staff account"
          itemLabel="staff email"
          type="email"
        />
        <p className="ps-muted">Up to {POS_EXTRA_STAFF_MAX} accounts per application.</p>
        {running && (
          <div className="ps-running">
            <span>
              {running.freeStaffCount > 0 && `${running.freeStaffCount} free · `}
              {running.paidStaffCount} × {formatMoney(settings.extraStaffPrice, config)}
            </span>
            <strong>{formatMoney(running.extraStaffTotal, config)}</strong>
          </div>
        )}
      </div>
    );
  };

  /** @returns {JSX.Element} the current step's buttons */
  const renderFooter = () => {
    if (step === 'done' || loading) {
      return (
        <>
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-primary" onClick={onClose}>Close</button>
        </>
      );
    }
    if (step === 'plans') {
      return (
        <>
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose}>Cancel</button>
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-primary" onClick={() => setStep('staff')} disabled={!plan}>Continue</button>
        </>
      );
    }
    return (
      <>
        <button type="button" className="pos-btn pos-btn-ghost" onClick={() => { setError(''); setStep('plans'); }}>Back</button>
        <span className="pos-foot-spacer" />
        <button type="button" className="pos-btn pos-btn-primary" onClick={handleStaffContinue}>Proceed to checkout</button>
      </>
    );
  };

  return createPortal(
    <div className="pos-modal-overlay">
      <div className="pos-modal ps-subscribe" role="dialog" aria-modal="true" aria-label="Subscribe">
        <header className="pos-modal-head">
          <h3>{step === 'done' ? 'Thank you' : 'Choose your plan'}</h3>
          <button
            type="button"
            className="pos-modal-close pos-modal-close-text"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pos-modal-body">
          {step !== 'done' && !loading && (
            <ol className="ps-steps">
              {STEPS.map((entry, index) => (
                <li key={entry.key} className={index === stepIndex ? 'current' : (index < stepIndex ? 'done' : '')}>
                  {entry.label}
                </li>
              ))}
            </ol>
          )}
          {renderBody()}
          {error && <p className="pa-role-error">{error}</p>}
        </div>

        {!checkingOut && <footer className="pos-modal-foot">{renderFooter()}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export default PosSubscribeModal;
